import type { CampaignSummary } from './outcomes/campaign.schema';

/**
 * Persistent, server-backed campaign selector (SCRUM-71). A tab-style row of buttons — one per
 * persisted campaign — that replaces the session-scoped recent-campaign chips, so a campaign
 * sent in an earlier session is still selectable today. Fed by the token-gated `useCampaigns`
 * query (GET /api/campaigns); clicking a button opens that campaign's outcome-tracking detail
 * view via the page's `activeCampaignId` gate.
 *
 * AC (SCRUM-71): each button shows the campaign NAME and DATE; the active campaign uses a
 * filled style, inactive campaigns an outline style. SCRUM-803: rendered full-width in the
 * content band, the row WRAPS to additional lines when the chips exceed one row (no horizontal
 * scrollbar) — never forcing page overflow (.claude/rules/frontend.md responsive rule). Renders nothing when
 * there are no campaigns (no empty-box flash) — the ledger shows normally.
 */
export function CampaignSelector({
  campaigns,
  activeId,
  onSelect,
}: {
  campaigns: readonly CampaignSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  if (campaigns.length === 0) {
    return null;
  }

  // SCRUM-803: full-width WRAPPING row (newest-first per the server order). The selector now
  // lives in the content band (not the narrow PageHeader actions slot), so it has the full
  // content width to work with — chips wrap to additional lines when they exceed one row
  // rather than scrolling horizontally (user preference over the SCRUM-801 scroll strip). All
  // campaigns stay reachable (no hard cap) — the point of the SCRUM-71 cross-session selector.
  return (
    <div className="flex w-full flex-wrap items-center gap-1.5">
      {campaigns.map((c) => {
        const active = c.id === activeId;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              onSelect(c.id);
            }}
            aria-pressed={active}
            title={`${String(c.targetCount)} customer${c.targetCount === 1 ? '' : 's'} · view outcomes`}
            className={`rounded-full px-2.5 py-1 text-xs whitespace-nowrap transition-colors ${
              active
                ? 'bg-brand-primary text-white'
                : 'border-border-1 text-text-1 hover:border-border-2 hover:text-text-0 border'
            }`}
          >
            {displayName(c.name)}
            <span className={active ? 'text-white/70' : 'text-text-3'}>
              {' '}
              · {formatMonth(c.createdAt)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Campaign-level date granularity for the selector chip (e.g. "Feb 2026"), matching the
 *  05-campaigns.html mock. Distinct from the outcome table's per-row "MMM D" format. */
function formatMonth(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * SCRUM-801: the wizard auto-name is "<Product> Outreach · <Mon Year>", so the name already
 * ends with a month/year — appending formatMonth(createdAt) would print the date twice. Strip a
 * trailing " · <Mon Year>" separator segment from the name so the authoritative createdAt date
 * (rendered separately) is the single date shown. Seed/plain names (no trailing date) pass
 * through unchanged. The regex matches the app's own " · " separator followed by a 3-letter
 * month and a 4-digit year at the end of the string.
 */
function displayName(name: string): string {
  return name.replace(/\s*·\s*[A-Za-z]{3,}\s+\d{4}\s*$/u, '').trim();
}
